import { AfterContentChecked, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ColumnMode, DatatableComponent } from '@siemens/ngx-datatable';
import { Page } from '../../../../core/components/table-footer-paging/table-footer-paging.component';
import { ColumnWidthsService } from '../../../../core/services/column-widths.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { JobsV2Service } from '../../../../core/services/jobsV2.service';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { FeatureService, PermissionService } from '../../../../core/services/permission';
import { StoresService } from '../../../../core/services/stores';
import { TablesHelperService } from '../../../../core/services/tables-helper.service';
import { ExportFormat, ExportPurchaseOrdersOptions, PurchaseOrder, Tables } from '../../../../data/models';
import { RepoExportService } from '../../../../data/repositories/repo-export.service';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { RepoSettingsService } from '../../../../data/repositories/repo-settings.service';
import { LayoutService } from '../../../layout/layout.service';
import { QuoteRequestsService } from '../../../quoterequests/quoterequest.service';
import { SearchPurchaseOrdersVariables } from '../domain/api/queries/SearchPurchaseOrders.generated';
import { PurchaseOrdersInvoiceService } from '../purchase-orders-invoice.service';
import { PurchaseOrdersService } from '../purchase-orders.service';

type TableColWidths = {
    OrderNumber: number;
    Description: number;
    SupplierInvoiceRef: number;
    SupplierDescription: number;
    OrderDate: number;
    RequiredByDateLocal: number;
    OrderTotalExTax: number;
    OrderType: number;
    OrderStatus: number;
    'Job/Description': number;
} & Record<string | number, number>;

@UntilDestroy()
@Component({
    selector: 'app-purchase-orders2',
    templateUrl: './purchase-orders2.component.html',
    standalone: false,
})
export class PurchaseOrders2Component implements OnInit, AfterContentChecked {
    title = 'All Orders';
    rows = [];
    selected = [];
    isLoading = false;
    // @ts-ignore TS2564
    isOrderEmailVisible: boolean;
    // @ts-ignore TS2564
    page: Page;
    // @ts-ignore TS2564
    search: string;
    // @ts-ignore TS7008
    currentComponentWidth;
    defaultSort = {} as { orderBy?: string; orderByDesc?: string };
    // @ts-ignore TS2564
    sortField: string;
    tableColumnMode: 'force' | 'flex' = 'flex';
    ordersWidths = {} as TableColWidths;

    // url params
    // @ts-ignore TS7008
    status;

    // @ts-ignore TS2564
    @ViewChild('myTable') table: DatatableComponent;
    // @ts-ignore TS7008
    @ViewChild('tableWrapper') tableWrapper;

    simpleItems = [
        {
            groupName: undefined as unknown as string,
            actions: [{ label: 'Reset View to Default', condition: true, value: 0 }],
        },
    ];

    moreActions = [
        {
            value: 0,
            label: 'Export to Excel',
            condition: true,
        },
    ];

    constructor(
        private settings: RepoSettingsService,
        public permissionService: PermissionService,
        private jobs: RepoJobsService,
        private featureService: FeatureService,
        public layoutService: LayoutService,
        private localStorageService: LocalStorageService,
        private columnWidthsService: ColumnWidthsService,
        private repoExportService: RepoExportService,
        private orderService: PurchaseOrdersService,
        private orderInvoiceService: PurchaseOrdersInvoiceService,
        private tablesHelperService: TablesHelperService,
        private jobsV2Service: JobsV2Service,
        private quoteReqService: QuoteRequestsService,
        private storesService: StoresService,
        private dialogService: DialogService,
        private route: ActivatedRoute
    ) {}

    ngOnInit() {
        if (this.localStorageService.getObject('orders-table')) {
            this.tableColumnMode = 'force';
            this.ordersWidths = this.localStorageService.getObject('orders-table');
        }

        this.page = new Page();
        this.setDefaults();
        this.setPage({ offset: 0 });
        this.load();

        if (this.layoutService.isMobile()) {
            this.tableColumnMode = 'force';
        } else if (!this.localStorageService.getObject('orders-table')) {
            this.tableColumnMode = 'flex';
        }

        this.isOrderEmailVisible = this.featureService.isStateAvailable('jobs.maintain-purchase-orders.send-purchase-order');
        this.storesService.updatePOStatus$.pipe(untilDestroyed(this)).subscribe((po) => {
            this.updatePORow(po);
        });
    }

    ngAfterContentChecked() {
        if (this.table && this.tableWrapper.nativeElement.clientWidth !== this.currentComponentWidth) {
            this.currentComponentWidth = this.tableWrapper.nativeElement.clientWidth;
            this.rows = [...this.rows];
        }
    }

    setDefaults() {
        this.page.pageNumber = 0;
        this.page.size = this.settings.AllOrdersViewPageCount;

        this.defaultSort = {
            orderBy: 'OrderDate desc',
            orderByDesc: 'desc',
        };

        this.sortField = this.defaultSort.orderBy!;
    }

    // @ts-ignore TS7006
    pageLimit(event) {
        this.page.size = Number.parseInt(event.target.textContent);
        this.page.pageNumber = 0;
        this.settings.AllOrdersViewPageCount = this.page.size;
        this.loadPage();
    }

    load() {
        this.status = this.route.snapshot.queryParams.status;
        this.title = 'All Orders';
        this.loadPage();
    }

    loadPage() {
        this.isLoading = true;

        const request: SearchPurchaseOrdersVariables['request'] = {
            statuses: this.status,
            search: !this.search ? '' : this.search,
            orderType: '',
            orderBy: (this.defaultSort.orderBy ? this.defaultSort.orderBy : this.defaultSort.orderByDesc)?.split(' ')[0],
            orderByDesc: this.defaultSort.orderByDesc !== undefined,
            rowOffset: this.page.pageNumber * this.page.size,
            rowFetch: this.page.size,
        };

        this.jobsV2Service.searchPurchaseOrders(request).subscribe((result) => {
            // Convert from graphQL casing to Breeze casing, so we do not need to change the HTML
            // @ts-ignore TS7006
            result.data.searchPurchaseOrders.results = result.data.searchPurchaseOrders.results.map((i) => ({
                ID_PurchaseOrder: i.iD_PurchaseOrder,
                ID_Reference: i.iD_Reference,
                OrderNumber: i.orderNumber,
                Description: i.description,
                SupplierInvoiceRef: i.supplierInvoiceRef,
                SupplierDescription: i.supplierDescription,
                OrderDate: i.orderDate,
                RequiredByDateLocal: i.requiredByDateLocal,
                OrderTotalExTax: i.orderTotalExTax,
                OrderType: i.orderType,
                OrderStatus: i.orderStatus,
                HasInvoiceAccSyncData: i.hasInvoiceAccSyncData,
                Job: {
                    Number: i.job.number,
                    Description: i.job.description,
                    FullDescription: i.job.fullDescription,
                },
            }));

            this.resolveSearch(result.data!.searchPurchaseOrders);
            this.isLoading = false;
        });
    }

    // @ts-ignore TS7006
    resolveSearch(data) {
        this.page.totalElements = data.inlineCount;
        this.rows = data.results;
        setTimeout(() => {
            this.table.columnMode = ColumnMode.force;
        }, 2000);
    }

    // @ts-ignore TS7006
    setSearch(event) {
        this.search = event;
        this.page.pageNumber = 0;
        this.load();
    }

    updatePORow(po: PurchaseOrder) {
        // @ts-ignore TS2322
        const row: PurchaseOrder = this.rows.find((r: PurchaseOrder) => {
            return r.ID_PurchaseOrder === po.ID_PurchaseOrder;
        });
        if (row) {
            Object.keys(row).forEach((key) => {
                // @ts-ignore TS7053
                row[key] = po[key];
            });
        }
    }

    // @ts-ignore TS7006
    viewOrder(order) {
        Promise.all([this.jobs.Get(order.ID_Reference), this.jobs.GetPurchaseOrderForList(order.ID_PurchaseOrder)]).then((data) => {
            this.orderService.viewOrder(<PurchaseOrder>(<unknown>data[1].results[0])).catch(() => ({}));
        });
    }

    // @ts-ignore TS7006
    addOrderInvoice(order) {
        Promise.all([this.jobs.Get(order.ID_Reference), this.jobs.GetPurchaseOrderForList(order.ID_PurchaseOrder)]).then((data) => {
            this.orderInvoiceService.addOrderInvoice(<PurchaseOrder>(<unknown>data[1].results[0])).catch(() => ({}));
        });
    }

    // @ts-ignore TS7006
    printOrder(order) {
        this.jobs.GetPurchaseOrderForList(order.ID_PurchaseOrder).then((reply: any) => {
            this.orderService.printOrder(reply.results[0]);
        });
    }

    // @ts-ignore TS7006
    emailOrder(order) {
        this.jobs.GetPurchaseOrderForList(order.ID_PurchaseOrder).then((reply: any) => {
            this.orderService.emailOrder(reply.results[0]);
        });
    }

    // @ts-ignore TS7006
    deleteOrder(evt, order: PurchaseOrder) {
        this.jobs.GetPurchaseOrderForList(order.ID_PurchaseOrder).then((reply: any) => {
            order = reply.results[0];

            evt.stopPropagation();
            const params = {
                title: `Delete order ${order.OrderNumberString}?`,
                text: 'You will not be able to recover this order',
            };

            this.dialogService.show(params).then(
                () => {
                    order.IsDeleted = true;
                    this.jobs.Get(order.ID_Reference);
                    this.jobs.Save(null, 'Order has been deleted').then(() => {
                        this.jobs.SyncJobItemsWithPurchaseOrders(order.Job);
                        this.quoteReqService.unLinkPurchaseOrderFromRFQ(order);
                        this.resetPageNumber();
                        this.loadPage();
                    });
                },
                () => ({})
            );
        });
    }

    // @ts-ignore TS7006
    setPage(pageInfo) {
        this.page.pageNumber = pageInfo.offset;
        this.load();
    }

    // @ts-ignore TS7006
    onSort(event) {
        // event was triggered, start sort sequence
        if (event.sorts[0].prop === this.table.sorts[0].prop && event.sorts[0].dir === 'desc') {
            this.defaultSort = {
                orderByDesc: event.sorts[0].prop,
            };
        } else {
            this.defaultSort = {
                orderBy: event.sorts[0].prop,
            };
        }
        this.loadPage();
    }

    onResize() {
        const ordersColWidths = {} as TableColWidths;
        this.table._internalColumns.forEach((row) => {
            const colKey = row.prop;
            const colWidth = row.width;
            if (colKey && colWidth) ordersColWidths[colKey] = colWidth;
        });
        this.localStorageService.saveObject('orders-table', ordersColWidths);
    }

    // @ts-ignore TS7006
    resolveSettingsAction(value) {
        switch (value) {
            case 0: {
                this.isLoading = true;
                this.ordersWidths = {} as TableColWidths;
                this.resetColumnWidths();
                const tempRows = [...this.rows];
                this.rows.length = 0;

                setTimeout(() => {
                    this.rows = [...tempRows];
                    this.isLoading = false;
                }, 100);
                break;
            }
        }
    }

    resetColumnWidths() {
        this.columnWidthsService.resetTableUsingTableName(Tables.Orders);
    }

    exportListToExcel() {
        const options: ExportPurchaseOrdersOptions = {
            format: ExportFormat.XLSX,
            filter: this.search,
            status: this.status ? this.status.split('|') : null,
        };
        this.repoExportService.exportPurchaseOrders(options);
    }

    // @ts-ignore TS7006
    resolveAction(action) {
        switch (action) {
            case 0: {
                this.exportListToExcel();
                break;
            }
        }
    }

    private resetPageNumber() {
        if (this.tablesHelperService.isResetPageNumber(this.page.totalElements - 1, this.page.size, this.page.pageNumber))
            this.page.pageNumber--;
    }
}
